import React, { useState, useEffect } from 'react';
import { FaPlay, FaClock, FaYoutube, FaEye } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { sendApiRequest } from '../utils/api';

const HowToUse = () => {
  const [currentVideo, setCurrentVideo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchTutorials = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await sendApiRequest('api/tutorials', {}, 'GET');

      const transformedVideos = response.data.map(video => ({
        ...video,
        thumbnail: `https://img.youtube.com/vi/${getYoutubeId(video.videoUrl)}/hqdefault.jpg`,
        duration: '10:00',
        views: '1K',
        uploaded: 'Recently',
        description: video.title || 'Watch this tutorial video'
      }));

      setVideos(transformedVideos);

      // Set first video as default selected
      if (transformedVideos.length > 0) {
        setCurrentVideo(transformedVideos[0]);
      }

    } catch (err) {
      setError('Failed to fetch tutorials');
    } finally {
      setLoading(false);
    }
  };

  const getYoutubeId = (url) => {
    if (!url) return '';
    const regExp = /^.*(youtu.be\/|v\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : '';
  };

  const getEmbedUrl = (url) => {
    const youtubeId = getYoutubeId(url);
    return youtubeId
      ? `https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`
      : '';
  };

  useEffect(() => {
    fetchTutorials();
  }, []);

  const calculateTotalViews = () => {
    return videos.reduce((sum) => sum + 1000, 0);
  };

  if (loading) {
    return <div className="text-center py-20">Loading Tutorials...</div>;
  }

  if (error) {
    return <div className="text-center text-red-500 py-20">{error}</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-pink-50 px-6 py-12">

      {/* 🔥 Hero Section */}
      <div className="text-center mb-14">
        <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
          Learn How To Use
        </h1>
        <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
          Master the platform with our guided video tutorials.
        </p>
      </div>

      {/* 📊 Stats */}
      <div className="grid md:grid-cols-3 gap-6 mb-14">
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center border border-blue-100 hover:shadow-xl transition">
          <FaYoutube className="text-3xl text-blue-600 mx-auto mb-3" />
          <h3 className="text-2xl font-bold text-blue-700">{videos.length}</h3>
          <p className="text-gray-500">Tutorials</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 text-center border border-pink-100 hover:shadow-xl transition">
          <FaEye className="text-3xl text-pink-600 mx-auto mb-3" />
          <h3 className="text-2xl font-bold text-pink-700">{calculateTotalViews()}+</h3>
          <p className="text-gray-500">Views</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 text-center border border-blue-100 hover:shadow-xl transition">
          <FaClock className="text-3xl text-blue-600 mx-auto mb-3" />
          <h3 className="text-2xl font-bold text-blue-700">24/7</h3>
          <p className="text-gray-500">Access</p>
        </div>
      </div>

      {/* 🎥 Main Video */}
      {currentVideo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="rounded-3xl overflow-hidden shadow-2xl mb-14 border border-blue-100"
        >
          <iframe
            src={getEmbedUrl(currentVideo.videoUrl)}
            className="w-full h-[300px] md:h-[500px]"
            title={currentVideo.title}
            allowFullScreen
          />
          <div className="p-6 bg-gradient-to-r from-blue-600 to-pink-600 text-white">
            <h2 className="text-2xl font-bold">{currentVideo.title}</h2>
            <p className="mt-2 text-white/90">{currentVideo.description}</p>
          </div>
        </motion.div>
      )}

      {/* 🎬 Video Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {videos.map((video) => (
          <motion.div
            key={video._id}
            whileHover={{ scale: 1.04 }}
            onClick={() => setCurrentVideo(video)}
            className="cursor-pointer bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all border border-gray-100"
          >
            <div className="relative">
              <img
                src={video.thumbnail}
                alt={video.title}
                className="w-full h-48 object-cover"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-600 to-pink-600 flex items-center justify-center shadow-xl">
                  <FaPlay className="text-white" />
                </div>
              </div>

              <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                {video.duration}
              </div>
            </div>

            <div className="p-4">
              <h4 className="font-semibold text-gray-800 line-clamp-1 hover:text-pink-600 transition">
                {video.title}
              </h4>
              <div className="flex justify-between mt-3 text-sm text-gray-500">
                <span className="flex items-center">
                  <FaEye className="mr-1 text-blue-500" />
                  {video.views}
                </span>
                <span>{video.uploaded}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

    </div>
  );

};

export default HowToUse;
